/**
 * @engine/input — Input Action
 *
 * An action represents a game-level input (e.g., "Jump", "Move", "Fire").
 * Actions are bound to controls and emit callbacks when triggered.
 */

import type {
  ActionDef,
  ActionType,
  ActionPhase,
  ActionContext,
  ActionCallback,
  BindingDef,
  ProcessorDef,
  InteractionDef,
  ControlValue,
  ButtonState,
  Vec2Like,
  Vec3Like,
} from '../types';
import { Binding, CompositeBindingGroup } from './Binding';
import type { IInputProcessor } from '../processors/InputProcessor';
import type { IInputInteraction, InteractionState } from '../interactions/InputInteraction';
import { createProcessor } from '../processors';
import { createInteraction } from '../interactions';

/**
 * Input Action — a game-level input abstraction.
 */
export class InputAction {
  /** Action name (unique within ActionMap) */
  readonly name: string;

  /** Value type */
  readonly type: ActionType;

  /** All bindings for this action */
  readonly bindings: Binding[] = [];

  /** Composite binding groups (for WASD, etc.) */
  readonly compositeGroups = new Map<string, Binding[]>();

  /** Default processors applied to final value */
  private readonly defaultProcessors: IInputProcessor[] = [];

  /** Default interactions applied to all bindings */
  private readonly defaultInteractions: IInputInteraction[] = [];

  /** Is this action enabled */
  private _enabled = true;

  /** Current phase */
  private _phase: ActionPhase = 'waiting';

  /** Current processed value */
  private _value: ControlValue = false;

  /** Previous frame value (for interaction evaluation) */
  private _previousValue: ControlValue = false;

  /** Frame counter for transition detection (incremented at endFrame) */
  private _frameCounter = 0;
  /** Frame at which button was pressed (for wasPressed) */
  private _pressedAtFrame = -1;
  /** Frame at which button was released (for wasReleased) */
  private _releasedAtFrame = -1;

  /** Time when action started */
  private _startTime = 0;

  /** Active binding (one that triggered current state) */
  private _activeBinding: Binding | null = null;

  /** Last device ID that updated a binding */
  private _lastDeviceId = '';

  /** Interaction states per binding */
  private readonly interactionStates = new Map<Binding, Map<IInputInteraction, InteractionState>>();

  /** Callbacks */
  private readonly startedCallbacks: ActionCallback[] = [];
  private readonly performedCallbacks: ActionCallback[] = [];
  private readonly canceledCallbacks: ActionCallback[] = [];

  /** Raw binding values (before composite/processing) */
  private readonly bindingValues = new Map<Binding, ControlValue>();

  constructor(def: ActionDef) {
    this.name = def.name;
    this.type = def.type;
    this._enabled = def.enabled ?? true;

    // Create bindings
    for (const bindingDef of def.bindings) {
      const binding = new Binding(bindingDef);
      this.bindings.push(binding);

      // Track composite groups
      if (binding.group) {
        let group = this.compositeGroups.get(binding.group);
        if (!group) {
          group = [];
          this.compositeGroups.set(binding.group, group);
        }
        group.push(binding);
      }
    }

    // Default processors
    if (def.processors) {
      for (const procDef of def.processors) {
        this.defaultProcessors.push(createProcessor(procDef));
      }
    }

    // Default interactions
    if (def.interactions) {
      for (const intDef of def.interactions) {
        this.defaultInteractions.push(createInteraction(intDef));
      }
    }
  }

  // ============================================================================
  // Public State
  // ============================================================================

  get enabled(): boolean {
    return this._enabled;
  }

  set enabled(value: boolean) {
    if (this._enabled === value) return;
    this._enabled = value;
    if (!value) {
      this._phase = 'disabled';
      this.resetState();
    } else {
      this._phase = 'waiting';
    }
  }

  get phase(): ActionPhase {
    return this._phase;
  }

  /**
   * Read the current value with type inference
   */
  readValue<T extends ControlValue = ControlValue>(): T {
    return this._value as T;
  }

  /**
   * For button actions: is currently pressed
   */
  get isPressed(): boolean {
    if (this.type !== 'button') return false;
    return this._value === true;
  }

  /**
   * For button actions: was pressed this frame
   */
  get wasPressed(): boolean {
    if (this.type !== 'button') return false;
    return this._pressedAtFrame === this._frameCounter;
  }

  /**
   * For button actions: was released this frame
   */
  get wasReleased(): boolean {
    if (this.type !== 'button') return false;
    return this._releasedAtFrame === this._frameCounter;
  }

  /**
   * For button actions: get full button state
   */
  get buttonState(): ButtonState {
    const isPressed = this._value === true;
    return {
      isPressed,
      wasPressed: this.wasPressed,
      wasReleased: this.wasReleased,
      holdTime: isPressed ? this._startTime : 0,
    };
  }

  /**
   * For value actions: get numeric value
   */
  get valueFloat(): number {
    if (typeof this._value === 'number') return this._value;
    if (typeof this._value === 'boolean') return this._value ? 1 : 0;
    return 0;
  }

  /**
   * For axis2d actions: get Vec2 value
   */
  get vector2(): Vec2Like {
    if (typeof this._value === 'object' && 'x' in this._value && 'y' in this._value) {
      return this._value as Vec2Like;
    }
    return { x: 0, y: 0 };
  }

  /**
   * For axis3d actions: get Vec3 value
   */
  get vector3(): Vec3Like {
    if (typeof this._value === 'object' && 'x' in this._value && 'z' in this._value) {
      return this._value as Vec3Like;
    }
    return { x: 0, y: 0, z: 0 };
  }

  // ============================================================================
  // Callbacks
  // ============================================================================

  /**
   * Register callback for when action starts
   */
  onStarted(callback: ActionCallback): () => void {
    this.startedCallbacks.push(callback);
    return () => {
      const idx = this.startedCallbacks.indexOf(callback);
      if (idx >= 0) this.startedCallbacks.splice(idx, 1);
    };
  }

  /**
   * Register callback for when action is performed
   */
  onPerformed(callback: ActionCallback): () => void {
    this.performedCallbacks.push(callback);
    return () => {
      const idx = this.performedCallbacks.indexOf(callback);
      if (idx >= 0) this.performedCallbacks.splice(idx, 1);
    };
  }

  /**
   * Register callback for when action is canceled
   */
  onCanceled(callback: ActionCallback): () => void {
    this.canceledCallbacks.push(callback);
    return () => {
      const idx = this.canceledCallbacks.indexOf(callback);
      if (idx >= 0) this.canceledCallbacks.splice(idx, 1);
    };
  }

  // ============================================================================
  // Internal State Updates
  // ============================================================================

  /**
   * Update a binding's raw value (called by InputManager)
   */
  updateBinding(binding: Binding, rawValue: ControlValue, time: number, deviceId: string): void {
    if (!this._enabled || !binding.active) return;

    // Track last device
    this._lastDeviceId = deviceId;

    // Process through binding processors
    const processedValue = binding.processValue(rawValue);
    this.bindingValues.set(binding, processedValue);

    // Recompute composite values
    this.computeActionValue(time, deviceId);

    // Update interactions
    this.updateInteractions(binding, processedValue, time, deviceId);
  }

  /**
   * Compute the final action value from all bindings
   */
  private computeActionValue(time: number, deviceId: string): void {
    // Track previous value for transition detection
    const oldValue = this._value;

    switch (this.type) {
      case 'button':
        this._value = this.computeButtonValue();
        break;
      case 'value':
        this._value = this.computeAxisValue();
        break;
      case 'axis2d':
        this._value = this.computeVector2Value();
        break;
      case 'axis3d':
        this._value = this.computeVector3Value();
        break;
    }

    // Apply default processors
    for (const proc of this.defaultProcessors) {
      this._value = proc.process(this._value);
    }

    // Detect button transitions and record frame.
    // Use _frameCounter + 1 because endFrame() increments the counter
    // BEFORE the game logic (graph executor) reads wasPressed/wasReleased.
    // This ensures the transition is visible after inputManager.update() completes.
    if (this.type === 'button') {
      if (this._value === true && oldValue !== true) {
        this._pressedAtFrame = this._frameCounter + 1;
      } else if (this._value !== true && oldValue === true) {
        this._releasedAtFrame = this._frameCounter + 1;
      }
    }
  }

  private computeButtonValue(): boolean {
    // OR all button bindings together
    for (const binding of this.bindings) {
      if (!binding.active) continue;
      const value = this.bindingValues.get(binding);
      if (value === true || (typeof value === 'number' && value > 0.5)) {
        return true;
      }
    }
    return false;
  }

  private computeAxisValue(): number {
    // Use highest magnitude value
    let result = 0;
    for (const binding of this.bindings) {
      if (!binding.active || binding.group) continue;
      const value = this.bindingValues.get(binding);
      if (typeof value === 'number' && Math.abs(value) > Math.abs(result)) {
        result = value;
      }
    }

    // Check composite 1D axis groups
    for (const [, group] of this.compositeGroups) {
      let compositeValue = 0;
      for (const binding of group) {
        if (!binding.active) continue;
        const value = this.bindingValues.get(binding);
        const numValue = value === true ? 1 : (typeof value === 'number' ? value : 0);

        if (binding.compositePart === 'positive') {
          compositeValue += numValue;
        } else if (binding.compositePart === 'negative') {
          compositeValue -= numValue;
        }
      }
      if (Math.abs(compositeValue) > Math.abs(result)) {
        result = compositeValue;
      }
    }

    return Math.max(-1, Math.min(1, result));
  }

  private computeVector2Value(): Vec2Like {
    let result: Vec2Like = { x: 0, y: 0 };
    let maxMagnitude = 0;
    // Absolute-position controls (`<Mouse>/position`, touch position) must NOT be normalized —
    // only digital→analog composites are. Direct Vec2 bindings pass through verbatim.
    let directVec = false;

    // Check direct Vec2 bindings (sticks, pointer position)
    for (const binding of this.bindings) {
      if (!binding.active || binding.group) continue;
      const value = this.bindingValues.get(binding);
      if (typeof value === 'object' && 'x' in value && 'y' in value) {
        const vec = value as Vec2Like;
        const mag = Math.sqrt(vec.x * vec.x + vec.y * vec.y);
        if (mag > maxMagnitude) {
          result = vec;
          maxMagnitude = mag;
          directVec = true;
        }
      }
    }

    // Check composite 2D groups (WASD)
    for (const [, group] of this.compositeGroups) {
      const composite = { x: 0, y: 0 };
      for (const binding of group) {
        if (!binding.active) continue;
        const value = this.bindingValues.get(binding);
        const numValue = value === true ? 1 : (typeof value === 'number' ? value : 0);

        switch (binding.compositePart) {
          case 'up':
            composite.y += numValue;
            break;
          case 'down':
            composite.y -= numValue;
            break;
          case 'right':
            composite.x += numValue;
            break;
          case 'left':
            composite.x -= numValue;
            break;
        }
      }

      const mag = Math.sqrt(composite.x * composite.x + composite.y * composite.y);
      if (mag > maxMagnitude) {
        result = composite;
        maxMagnitude = mag;
        directVec = false;
      }
    }

    // Normalize if magnitude > 1 — digital→analog conversion for COMPOSITES only. A direct
    // Vec2 control (mouse/touch position in pixels) is an absolute value and passes through.
    if (!directVec) {
      const mag = Math.sqrt(result.x * result.x + result.y * result.y);
      if (mag > 1) {
        result = { x: result.x / mag, y: result.y / mag };
      }
    }

    return result;
  }

  private computeVector3Value(): Vec3Like {
    // Similar to Vec2 but for 3D sensors
    for (const binding of this.bindings) {
      if (!binding.active) continue;
      const value = this.bindingValues.get(binding);
      if (typeof value === 'object' && 'x' in value && 'z' in value) {
        return value as Vec3Like;
      }
    }
    return { x: 0, y: 0, z: 0 };
  }

  // ============================================================================
  // Interactions
  // ============================================================================

  private updateInteractions(
    binding: Binding,
    value: ControlValue,
    time: number,
    deviceId: string
  ): void {
    const interactions = binding.interactions.length > 0
      ? binding.interactions
      : this.defaultInteractions;

    if (interactions.length === 0) {
      // No interactions — emit based on value changes
      this.emitSimpleCallbacks(value, time, binding.path, deviceId);
      return;
    }

    // Get or create interaction states for this binding
    let states = this.interactionStates.get(binding);
    if (!states) {
      states = new Map();
      this.interactionStates.set(binding, states);
    }

    for (const interaction of interactions) {
      let state = states.get(interaction);
      if (!state) {
        state = interaction.createState();
        states.set(interaction, state);
      }

      const ctx: ActionContext = {
        action: this.name,
        phase: this._phase,
        value,
        duration: time - this._startTime,
        control: binding.path,
        device: deviceId,
        time,
      };

      const newPhase = interaction.process(state, value, time, ctx);

      if (newPhase !== this._phase) {
        this.transitionPhase(newPhase, ctx);
      }
    }
  }

  private emitSimpleCallbacks(
    value: ControlValue,
    time: number,
    control: string,
    deviceId: string
  ): void {
    const isActive = this.isValueActive(value);
    const wasActive = this.isValueActive(this._previousValue);

    const ctx: ActionContext = {
      action: this.name,
      phase: this._phase,
      value: this._value,
      duration: time - this._startTime,
      control,
      device: deviceId,
      time,
    };

    if (isActive && !wasActive) {
      this._startTime = time;
      this.transitionPhase('started', ctx);
      this.transitionPhase('performed', ctx);
    } else if (!isActive && wasActive) {
      this.transitionPhase('canceled', ctx);
    }
  }

  private isValueActive(value: ControlValue): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return Math.abs(value) > 0.001;
    if (typeof value === 'object' && 'x' in value) {
      const vec = value as { x: number; y: number; z?: number };
      const mag = Math.sqrt(vec.x * vec.x + vec.y * vec.y + (vec.z ?? 0) ** 2);
      return mag > 0.001;
    }
    return false;
  }

  private transitionPhase(newPhase: ActionPhase, ctx: ActionContext): void {
    this._phase = newPhase;
    ctx = { ...ctx, phase: newPhase };

    switch (newPhase) {
      case 'started':
        for (const cb of this.startedCallbacks) cb(ctx);
        break;
      case 'performed':
        for (const cb of this.performedCallbacks) cb(ctx);
        break;
      case 'canceled':
        for (const cb of this.canceledCallbacks) cb(ctx);
        this._phase = 'waiting';
        break;
    }
  }

  // ============================================================================
  // Frame Management
  // ============================================================================

  /**
   * Begin frame — called before input updates.
   * Snapshots _previousValue for interaction evaluation.
   */
  beginFrame(): void {
    this._previousValue = this._value;
  }

  /**
   * End frame — commit state and re-evaluate time-based interactions
   */
  endFrame(time: number): void {
    // Re-evaluate interactions for active bindings (for time-based interactions like hold)
    this.reevaluateInteractions(time);
    // Advance frame counter — between-frame events and poll events
    // that set _pressedAtFrame = _frameCounter will be visible until
    // endFrame increments the counter.
    this._frameCounter++;
  }

  /**
   * Re-evaluate interactions for all bindings with active values.
   * This is needed for time-based interactions (e.g., hold) to detect
   * when hold duration is reached even without new input events.
   */
  private reevaluateInteractions(time: number): void {
    if (!this._enabled) return;

    for (const binding of this.bindings) {
      if (!binding.active) continue;
      
      const value = this.bindingValues.get(binding);
      if (value === undefined) continue;

      // Only re-evaluate if there are interactions
      const interactions = binding.interactions.length > 0
        ? binding.interactions
        : this.defaultInteractions;
      
      if (interactions.length === 0) continue;

      // Get interaction states for this binding
      const states = this.interactionStates.get(binding);
      if (!states) continue;

      for (const interaction of interactions) {
        const state = states.get(interaction);
        if (!state) continue;

        const ctx: ActionContext = {
          action: this.name,
          phase: this._phase,
          value,
          duration: time - this._startTime,
          control: binding.path,
          device: this._lastDeviceId,
          time,
        };

        const newPhase = interaction.process(state, value, time, ctx);

        if (newPhase !== this._phase) {
          this.transitionPhase(newPhase, ctx);
        }
      }
    }
  }

  /**
   * Reset action state
   */
  resetState(): void {
    this._value = this.getDefaultValue();
    this._previousValue = this._value;
    this._frameCounter = 0;
    this._pressedAtFrame = -1;
    this._releasedAtFrame = -1;
    this._phase = this._enabled ? 'waiting' : 'disabled';
    this._activeBinding = null;
    this.bindingValues.clear();
    this.interactionStates.clear();
  }

  private getDefaultValue(): ControlValue {
    switch (this.type) {
      case 'button':
        return false;
      case 'value':
        return 0;
      case 'axis2d':
        return { x: 0, y: 0 };
      case 'axis3d':
        return { x: 0, y: 0, z: 0 };
    }
  }

  // ============================================================================
  // Serialization
  // ============================================================================

  toDefinition(): ActionDef {
    return {
      name: this.name,
      type: this.type,
      bindings: this.bindings.map((b) => b.toDefinition()),
      processors: this.defaultProcessors.length > 0
        ? this.defaultProcessors.map((p) => p.toDefinition())
        : undefined,
      interactions: this.defaultInteractions.length > 0
        ? this.defaultInteractions.map((i) => i.toDefinition())
        : undefined,
      enabled: this._enabled ? undefined : false,
    };
  }

  static fromDefinition(def: ActionDef): InputAction {
    return new InputAction(def);
  }
}
